import React, { useCallback, useEffect, useRef, useState } from "react";

import { fetchAdminInvoices } from "../../../../api/adminData";
import { fetchBexioTenantInvoices } from "../../../../api/adminData/bexio";
import BexioTablePagination from "../../../../components/admin/bexio/workspace/BexioTablePagination";
import StatusPill from "../../../../components/admin/bexio/BexioInvoiceStatusPill";
import {
  PROVIDER_MISSING_LABEL,
  PROVIDER_MISSING_LABEL_SHORT,
  fmtInvoiceMoney,
} from "../../../../components/admin/bexio/bexioFormat";
import { formatDateOnly } from "../../../../utils/datetime";
import InvoiceSourceSwitcher from "../../invoices/InvoiceSourceSwitcher";
import {
  linkSourceLabel,
  rentPeriodLabel,
} from "../../tenancies/tenancyFinance";
import { Card, EmptyState, show } from "../../tenancies/workspaceUi";
import { formatInvoiceAmount } from "../tenantDetailHelpers";
import useTenantInvoiceSource from "./tenantInvoiceSource";

/**
 * The tenant detail's "Rechnungen" tab (TINV-2).
 *
 * ONE VIEW, ONE WORLD. An organization creates its invoices either in Vantio or
 * in Bexio, and that stored intention decides which world is CURRENT here.
 * Bexio-billing organizations get their Bexio invoices by default and may look
 * back at the Vantio archive; Vantio-billing organizations have one world and
 * get no switch at all. The two are NEVER summed, concatenated or interleaved:
 * they were never simultaneously in force, so a union of them would count
 * nothing real.
 *
 * WHAT THIS REPLACES. Until TINV-2 the tab read `GET /api/invoices?tenant_id=`
 * unconditionally, at PAGE MOUNT, for a tab most readers never open — and that
 * table stops gaining rows the moment an organization's
 * `invoice_creation_provider` becomes `bexio`, because the native creation path
 * refuses to write to it. The visible result was a tab that said "Keine
 * Rechnungen vorhanden" to a tenant with a full Bexio ledger. Both halves of
 * that are fixed here: the world follows the provider, and the read waits for
 * the tab.
 *
 * THE READ IS GATED TWICE. `active` (the tab is open) AND a confirmed source.
 * While the capability answer is in flight, and after it failed, nothing is
 * fetched and no world is named — a page that guessed "Vantio" on a failed read
 * would show an organization its archive labelled as its current books.
 *
 * SERVER PAGINATION, NOT A SLICED WINDOW. Both worlds page against
 * `limit`/`offset` (`skip` on the native route) and both report a `total`
 * counted over the same set, so the footer describes the set the page came
 * from. The old tab loaded the newest twenty and paged THOSE in the browser,
 * which told a tenant with forty-five invoices about twenty.
 *
 * NO NEW STATUS AXIS, NO DERIVED MONTH, NO INVENTED CURRENCY. Each row prints
 * what its own wire carries. The Bexio rows use the repo's existing Bexio
 * vocabulary; the Vantio rows keep theirs (see `NativeInvoiceRow`). Nothing
 * here computes overdue, reconstructs a payment from a subtraction, or turns a
 * date into a rent month.
 */

/** One page. Ten, as the tab has always shown and as the tenancy Finanzen tab does. */
const PAGE_SIZE = 10;

/**
 * A stable React key for either world's row.
 *
 * A Bexio invoice always carries a uuid. A Vantio one always carries an id too,
 * but the panel this replaces defended against a missing one, and dropping that
 * defence would turn an absent id from a cosmetic oddity into duplicate keys
 * across the whole page — so the fallback moves across with the markup.
 */
const rowKey = (inv) =>
  inv?.id != null ? String(inv.id) : `${inv?.invoice_number}-${inv?.due_date}`;

/* ── Bexio rows ──────────────────────────────────────────────────────── */

/**
 * The month, or nothing at all — never a date standing in for one.
 *
 * `rent_period` reaches the wire from `bexio_invoice_draft_requests.rent_period`
 * and from nowhere else, so it is NULL for every hand-mapped historical invoice.
 * That renders as an em dash. It is never derived from `issue_date`, `due_date`,
 * `document_nr` or the title — each of which would be a guess printed as a fact,
 * and two of which are visible in their own right one line below.
 */
function RentPeriodCell({ invoice }) {
  if (invoice.rent_period_conflict) {
    return (
      <span
        data-testid="tenant-invoice-period-conflict"
        title="Mehrere Rechnungsentwürfe dieses Mieters nennen unterschiedliche Monate für diese Rechnung."
        className="text-[12px] text-amber-800 dark:text-amber-300"
      >
        Monat unklar
      </span>
    );
  }
  const label = rentPeriodLabel(invoice.rent_period);
  if (!label) {
    return <span className="text-[12px] text-muted-foreground">—</span>;
  }
  return <span className="text-[12px] text-foreground">{label}</span>;
}

/**
 * A completed provider inventory looked for this document at Bexio and did not
 * find it. Independent of `provider_replaced`: either may be true alone, both
 * may be true, and neither is computed from the other.
 *
 * Amber and never "gelöscht" — what we persist is "an inventory looked and did
 * not find it"; deletion is only the likeliest reason, never one we observed.
 */
function ProviderMissingTag({ invoice }) {
  if (invoice?.provider_missing_at == null) return null;
  return (
    <span
      data-testid="tenant-invoice-provider-missing"
      title={PROVIDER_MISSING_LABEL}
      className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-[1px] text-[10px] font-semibold text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
    >
      {PROVIDER_MISSING_LABEL_SHORT}
    </span>
  );
}

/**
 * The outstanding part, only where it says something the amount does not.
 *
 * Same rule and same words as the invoice dashboard's `MoneyCell`: a fully
 * outstanding invoice would merely repeat its own total. `total_remaining` is
 * the provider's own figure and is printed as given — never reconstructed as
 * `total - total_paid`, which would be a second opinion about a number Bexio
 * already reported.
 */
function OpenAmount({ invoice }) {
  const remaining = invoice.total_remaining;
  if (remaining == null) return null;
  if (!(Number(remaining) > 0)) return null;
  if (Number(remaining) === Number(invoice.total)) return null;
  return (
    <span className="tabular-nums" data-testid="tenant-invoice-open-amount">
      davon offen:{" "}
      {fmtInvoiceMoney(remaining, invoice.currency, { marker: false })}
    </span>
  );
}

function BexioInvoiceRow({ invoice }) {
  return (
    <li
      data-testid={`tenant-invoice-${invoice.id}`}
      className="flex flex-col gap-1 border-b border-border px-4 py-[10px] last:border-b-0"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
          {show(invoice.document_nr)}
          {invoice.title ? (
            <span className="font-normal text-muted-foreground">
              {" · "}
              {invoice.title}
            </span>
          ) : null}
        </span>
        <StatusPill status={invoice.bexio_status} />
        {/* `fmtInvoiceMoney`, never `fmtMoney` and never a `|| "CHF"`:
            `bexio_invoices.currency` has been nullable since migration 159, and
            NULL is a third state — not CHF, not a foreign code, but "the
            provider never told us". Printing CHF over it would re-assert in the
            UI exactly the claim that migration removed from the database. */}
        <span className="shrink-0 tabular-nums text-[13px] text-foreground">
          {fmtInvoiceMoney(invoice.total, invoice.currency)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
        <RentPeriodCell invoice={invoice} />
        <span>Rechnungsdatum: {formatDateOnly(invoice.issue_date)}</span>
        <span>Fällig: {formatDateOnly(invoice.due_date)}</span>
        <OpenAmount invoice={invoice} />
        {linkSourceLabel(invoice.tenancy_link_source) ? (
          <span data-testid="tenant-invoice-link-source">
            {linkSourceLabel(invoice.tenancy_link_source)}
          </span>
        ) : null}
        {/* The ledger says Bexio deleted this document and raised a replacement
            for the same month. Both rows carry that month's money and only one
            of them is real — so it is marked, never silently dropped. */}
        {invoice.provider_replaced ? (
          <span
            data-testid="tenant-invoice-replaced"
            className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-[1px] text-[10px] font-semibold text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
          >
            Ersetzt
          </span>
        ) : null}
        <ProviderMissingTag invoice={invoice} />
        {/* Several of THIS tenant's tenancies claim this one document, so the
            backend withheld `resolved_tenancy_id` rather than picking one. Said
            in words and not as an id: there is no tenancy column here — naming
            one would need a request per invoice — so a raw uuid would be an
            identifier the reader cannot resolve, printed where a fact belongs. */}
        {invoice.tenancy_attribution_conflict ? (
          <span
            data-testid="tenant-invoice-tenancy-conflict"
            title="Mehrere Mietverhältnisse dieses Mieters beanspruchen diese Rechnung."
            className="text-amber-800 dark:text-amber-300"
          >
            Mietverhältnis unklar
          </span>
        ) : null}
      </div>
    </li>
  );
}

/* ── Vantio (native) rows ────────────────────────────────────────────── */

/**
 * A Vantio invoice, rendered exactly as this tab has always rendered it.
 *
 * DELIBERATELY UNCHANGED, and the reasoning is worth recording. Two of its
 * properties are known imperfections, both pinned by TINV-0: the pill prints the
 * backend token (`unpaid`) where the invoice dashboard says "Offen", and
 * `formatInvoiceAmount` defaults a missing currency to CHF. Neither is fixed
 * here.
 *
 * Fixing the first means one of two things, and both are worse than waiting:
 * exporting `InvoiceTable`'s private `STATUS` map out of a large Rechnungen-2.0
 * dashboard file, which is scope this slice was told to stay out of; or writing
 * a second native status vocabulary under `tenant/`, which is the duplication
 * the same brief forbids. Fixing the second changes how a Vantio amount reads
 * across the product, and that belongs with the native-invoice retirement, not
 * with a Bexio migration.
 *
 * So this is a pure MOVE of the previous markup, and TINV-0's A3/A4/A3b
 * assertions survive against it — they simply became contracts of the VANTIO
 * SOURCE rather than of the tab.
 */
function NativeInvoiceRow({ invoice }) {
  const st = String(invoice.status || "").toLowerCase();
  const statusClass =
    st === "paid" || st === "bezahlt"
      ? "border-[rgba(61,220,132,0.2)] bg-[rgba(61,220,132,0.1)] text-[#3ddc84]"
      : st === "overdue" || st === "überfällig"
        ? "border-[rgba(255,95,109,0.2)] bg-[rgba(255,95,109,0.1)] text-[#ff5f6d]"
        : st === "open" || st === "offen" || st === "unpaid"
          ? "border-[rgba(245,166,35,0.2)] bg-[rgba(245,166,35,0.1)] text-[#f5a623]"
          : "border-[#1c2035] bg-[#191c28] text-[#8892b0]";
  return (
    <li
      data-testid={`tenant-invoice-${invoice.id}`}
      className="flex items-center gap-[10px] border-b border-border px-4 py-[10px] last:border-b-0"
    >
      <span className="flex-1 font-mono text-[10px] text-[#5b9cf6]">
        {invoice.invoice_number || "—"}
      </span>
      <span className="font-mono text-[11px] font-medium text-foreground">
        {formatInvoiceAmount(invoice.amount, invoice.currency)}
      </span>
      <span className="font-mono text-[10px] text-muted-foreground">
        {formatDateOnly(invoice.due_date)}
      </span>
      <span
        className={`inline-flex items-center rounded-full border px-2 py-[2px] text-[9px] font-semibold ${statusClass}`}
      >
        {invoice.status || "—"}
      </span>
    </li>
  );
}

/* ── States ──────────────────────────────────────────────────────────── */

/**
 * "There are none" — said in the terms of the world the reader is in.
 *
 * The old single sentence ("Keine Rechnungen vorhanden") could not tell "this
 * tenant has no invoices" from "the invoices are in Bexio and this tab was
 * reading the wrong table", and under a Bexio provider it was reliably the
 * second. Neither sentence claims anything the read does not prove: no "alles
 * bezahlt", no "keine offenen Rechnungen", no "nicht synchronisiert".
 */
function SourceEmptyState({ source }) {
  return (
    <EmptyState
      testId={`tenant-invoices-empty-${source}`}
      title={
        source === "bexio"
          ? "Noch keine Bexio-Rechnungen für diesen Mieter vorhanden."
          : "Keine Vantio-Rechnungen für diesen Mieter vorhanden."
      }
    />
  );
}

function ListSkeleton() {
  return (
    <div
      className="flex flex-col gap-2 p-4"
      aria-busy="true"
      data-testid="tenant-invoices-loading"
    >
      <span className="sr-only">Rechnungen werden geladen …</span>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          aria-hidden="true"
          className="h-[38px] animate-pulse rounded-[7px] border border-border bg-muted/40"
        />
      ))}
    </div>
  );
}

/* ── The tab ─────────────────────────────────────────────────────────── */

export default function TenantInvoicesTab({
  tenantId,
  active,
  provider,
  capabilitiesLoading = false,
  capabilitiesError = null,
}) {
  const { source, provider: confirmedProvider, labels, setSource } =
    useTenantInvoiceSource({
      loading: capabilitiesLoading,
      error: capabilitiesError,
      provider,
    });

  const [list, setList] = useState({
    loading: false,
    error: null,
    items: [],
    total: 0,
    offset: 0,
  });

  // The (tenant, world) pair whose first page is loaded or loading. Stops a
  // refetch on every re-render and on every return to the tab.
  const loadedFor = useRef(null);
  // Monotonic, because `loadedFor` alone cannot order two reads of the SAME
  // pair: paging twice quickly must not let the first answer overwrite the
  // second.
  const requestId = useRef(0);

  const load = useCallback((tid, src, offset) => {
    const rid = requestId.current + 1;
    requestId.current = rid;
    // Items are dropped as the request starts, so a rendered row can never
    // belong to a world or a tenant other than the one being loaded.
    setList({ loading: true, error: null, items: [], total: 0, offset });

    const request =
      src === "bexio"
        ? fetchBexioTenantInvoices({
            tenantId: tid,
            limit: PAGE_SIZE,
            offset,
          })
        : // `skip`, not a client-side slice: the native route has always
          // paginated server-side and the envelope's `total` counts the whole
          // filtered set.
          fetchAdminInvoices({ tenantId: tid, limit: PAGE_SIZE, skip: offset });

    request
      .then((d) => {
        if (requestId.current !== rid) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        setList({
          loading: false,
          error: null,
          items,
          total: Number.isFinite(Number(d?.total))
            ? Number(d.total)
            : items.length,
          offset,
        });
      })
      .catch((e) => {
        if (requestId.current !== rid) return;
        setList({
          loading: false,
          error: e?.message || "Rechnungen konnten nicht geladen werden.",
          items: [],
          total: 0,
          offset,
        });
      });
  }, []);

  useEffect(() => {
    // THE GATE. Closed tab, no tenant, or no confirmed world: read nothing.
    if (!active || !tenantId || !source) return;
    const key = `${tenantId}::${source}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    load(tenantId, source, 0);
  }, [active, tenantId, source, load]);

  // The CLOSED-TAB path. When the tab is open, the effect above has already
  // claimed the new key and started page one, so this finds nothing stale to
  // drop. It matters when the tenant or the world changes while the panel is
  // mounted but hidden: without it `loadedFor` would still hold the old key and
  // reopening the tab would show the previous result under the new heading.
  // Offset returns to zero either way — a reload always starts there.
  useEffect(() => {
    const key = tenantId && source ? `${tenantId}::${source}` : null;
    if (loadedFor.current && loadedFor.current !== key) {
      loadedFor.current = null;
      requestId.current += 1;
      setList({ loading: false, error: null, items: [], total: 0, offset: 0 });
    }
  }, [tenantId, source]);

  const handleOffsetChange = useCallback(
    (offset) => {
      if (!tenantId || !source) return;
      load(tenantId, source, offset);
    },
    [tenantId, source, load]
  );

  const Row = source === "bexio" ? BexioInvoiceRow : NativeInvoiceRow;

  return (
    <div className="flex flex-col gap-3" data-testid="tenant-invoices">
      <Card
        title="Rechnungen"
        subtitle={
          source === "bexio"
            ? "Was Bexio zu diesem Mieter führt — über alle Mietverhältnisse hinweg. Ein Mietmonat steht nur dort, wo ein Rechnungsentwurf ihn festgehalten hat."
            : source === "native"
              ? "Die in Vantio erfassten Rechnungen dieses Mieters."
              : undefined
        }
        action={
          <InvoiceSourceSwitcher
            provider={confirmedProvider}
            source={source}
            labels={labels}
            onSelect={setSource}
          />
        }
      >
        {capabilitiesError ? (
          // Fail visibly. Falling back to the Vantio list here would show an
          // organization that bills in Bexio an archive labelled as its books.
          <p
            role="alert"
            data-testid="tenant-invoices-capabilities-error"
            className="m-0 text-[12.5px] text-rose-700 dark:text-rose-300"
          >
            Rechnungsquelle konnte nicht geladen werden.
          </p>
        ) : !source ? (
          <ListSkeleton />
        ) : list.loading ? (
          <ListSkeleton />
        ) : list.error ? (
          <p
            role="alert"
            data-testid="tenant-invoices-error"
            className="m-0 text-[12.5px] text-rose-700 dark:text-rose-300"
          >
            {list.error}
          </p>
        ) : list.items.length === 0 ? (
          <SourceEmptyState source={source} />
        ) : (
          <div className="-mx-4 -mb-4">
            <ul
              className="m-0 list-none p-0"
              data-testid={`tenant-invoices-list-${source}`}
            >
              {list.items.map((inv) => (
                <Row key={rowKey(inv)} invoice={inv} />
              ))}
            </ul>
            {/* The same offset pagination the Bexio workspace and the tenancy
                Finanzen tab use, against the same `limit`/`offset`/`total`
                contract. A page change is a new server read, so the footer and
                the rows always describe one set. */}
            <BexioTablePagination
              total={list.total}
              offset={list.offset}
              limit={PAGE_SIZE}
              onOffsetChange={handleOffsetChange}
              noun="Rechnungen"
              busy={list.loading}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
